import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    FiX, FiCheckCircle, FiChevronRight, FiChevronLeft,
    FiBriefcase, FiMapPin, FiDollarSign, FiUsers,
    FiClock, FiTag, FiCode, FiCalendar, FiInfo
} from 'react-icons/fi';
import { useTheme } from '../../../context/ThemeContext';

const CATEGORIES = ['Engineering', 'Marketing', 'Design', 'Content', 'Sales', 'Data Science', 'Other'];
const JOB_TYPES = ['Full-time', 'Internship', 'Part-time', 'Contract', 'Walk-in Interview', 'Assessment Drive'];
const WORK_MODES = ['Remote', 'On-site', 'Hybrid'];

const STEPS = [
    { label: 'Basic Info', icon: <FiBriefcase />, desc: 'Role title, type & location' },
    { label: 'Compensation', icon: <FiDollarSign />, desc: 'Salary, duration & skills' },
    { label: 'Requirements', icon: <FiUsers />, desc: 'Full job description' },
];

/* ── Pill option selector ──────────────────────────────────────── */
const PillSelect = ({ value, onChange, options }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {options.map(opt => (
            <motion.button
                key={opt}
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => onChange(opt)}
                style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: `1.5px solid ${opt === value ? 'var(--accent)' : 'var(--border)'}`,
                    background: opt === value ? 'rgba(34,197,94,0.12)' : 'var(--bg-card)',
                    color: opt === value ? 'var(--accent)' : 'var(--text-muted)',
                    fontWeight: opt === value ? 700 : 500,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                }}
            >
                {opt}
            </motion.button>
        ))}
    </div>
);

/* ── Field wrapper ─────────────────────────────────────────────── */
const Field = ({ label, required, icon, children, hint }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {icon && <span style={{ color: 'var(--accent)', opacity: 0.7, fontSize: 13 }}>{icon}</span>}
            <label style={{
                fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>
                {label} {required && <span style={{ color: 'var(--accent)' }}>*</span>}
            </label>
        </div>
        {children}
        {hint && <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0, opacity: 0.7 }}>{hint}</p>}
    </div>
);

/* ── Main Modal ───────────────────────────────────────────────────── */
const JobPostModal = ({ show, onClose, onSubmit, newJob, setNewJob, isEditing = false }) => {
    const { isDarkMode } = useTheme();
    const [step, setStep] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const h = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    useEffect(() => { if (show) setStep(1); }, [show]);

    if (!show) return null;

    const inputStyle = {
        padding: '12px 16px',
        background: 'var(--bg-card)',
        border: '1.5px solid var(--border)',
        borderRadius: '12px',
        color: 'var(--text-main)',
        fontSize: '14px',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s',
        fontFamily: 'inherit',
    };

    const isDrive = newJob.type === 'Walk-in Interview' || newJob.type === 'Assessment Drive';

    const TOTAL_STEPS = 3;

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? 0 : '20px',
        }}>
            <style>{`
                .jpm-input:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px rgba(34,197,94,0.1) !important; }
                .ql-toolbar.ql-snow {
                    background: ${isDarkMode ? 'var(--bg-surface)' : '#f8fafc'} !important;
                    border-color: var(--border) !important;
                    border-radius: 12px 12px 0 0 !important;
                    display: flex !important; flex-wrap: nowrap !important;
                    overflow-x: auto !important; white-space: nowrap !important;
                    scrollbar-width: none !important;
                    padding: 8px 10px !important;
                }
                .ql-container.ql-snow {
                    border-color: var(--border) !important;
                    background: var(--bg-card) !important;
                    color: var(--text-main) !important;
                    border-radius: 0 0 12px 12px !important;
                    font-family: inherit !important; font-size: 14px !important;
                }
                .ql-editor { color: var(--text-main) !important; min-height: ${isMobile ? '160px' : '200px'}; }
                .ql-editor.ql-blank::before { color: var(--text-muted) !important; font-style: normal !important; opacity: 0.5 !important; }
                .ql-snow .ql-stroke { stroke: var(--text-main) !important; }
                .ql-snow .ql-fill { fill: var(--text-main) !important; }
                .ql-snow .ql-picker { color: var(--text-main) !important; }
                .ql-snow .ql-picker-options { background: var(--bg-card) !important; border-color: var(--border) !important; }
            `}</style>

            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ type: 'spring', stiffness: 280, damping: 28 }}
                style={{
                    background: 'var(--bg-main)',
                    border: '1px solid var(--border)',
                    borderRadius: isMobile ? 0 : '24px',
                    width: '100%',
                    maxWidth: isMobile ? '100%' : '900px',
                    height: isMobile ? '100%' : 'auto',
                    maxHeight: isMobile ? '100%' : '88vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                }}
            >
                {/* ── Desktop: two-panel layout ── */}
                <div style={{
                    display: 'flex',
                    flex: 1,
                    overflow: 'hidden',
                    flexDirection: isMobile ? 'column' : 'row',
                }}>

                    {/* ── LEFT PANEL (desktop only): Step Guide ── */}
                    {!isMobile && (
                        <div style={{
                            width: '280px',
                            flexShrink: 0,
                            background: isDarkMode
                                ? 'linear-gradient(160deg, rgba(34,197,94,0.07) 0%, rgba(0,0,0,0.3) 100%)'
                                : 'linear-gradient(160deg, rgba(34,197,94,0.06) 0%, rgba(240,253,244,0.5) 100%)',
                            borderRight: '1px solid var(--border)',
                            padding: '32px 24px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}>
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '6px 12px', borderRadius: '8px',
                                    background: 'rgba(34,197,94,0.1)',
                                    border: '1px solid rgba(34,197,94,0.2)',
                                    marginBottom: '16px',
                                }}>
                                    <FiBriefcase size={12} color="var(--accent)" />
                                    <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                        {isEditing ? 'Edit Listing' : 'New Listing'}
                                    </span>
                                </div>
                                <h2 style={{ fontSize: '20px', fontWeight: 900, color: 'var(--text-main)', margin: 0, letterSpacing: '-0.03em' }}>
                                    {isEditing ? 'Update\nOpportunity' : 'Post an\nOpportunity'}
                                </h2>
                                {newJob.title && (
                                    <p style={{ fontSize: '12px', color: 'var(--accent)', marginTop: '8px', fontWeight: 600 }}>
                                        "{newJob.title}"
                                    </p>
                                )}
                            </div>

                            {/* Step indicators */}
                            {STEPS.map((s, idx) => {
                                const stepNum = idx + 1;
                                const isActive = step === stepNum;
                                const isDone = step > stepNum;
                                return (
                                    <motion.div
                                        key={s.label}
                                        whileHover={isDone ? { x: 2 } : {}}
                                        onClick={isDone ? () => setStep(stepNum) : undefined}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '12px 14px', borderRadius: '12px',
                                            background: isActive ? 'rgba(34,197,94,0.12)' : 'transparent',
                                            border: `1px solid ${isActive ? 'rgba(34,197,94,0.25)' : 'transparent'}`,
                                            cursor: isDone ? 'pointer' : 'default',
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <div style={{
                                            width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                                            background: isDone ? 'var(--accent)' : isActive ? 'rgba(34,197,94,0.15)' : 'var(--bg-surface)',
                                            border: `1.5px solid ${isActive || isDone ? 'var(--accent)' : 'var(--border)'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: isDone ? '#fff' : isActive ? 'var(--accent)' : 'var(--text-muted)',
                                            fontSize: '14px',
                                            fontWeight: 800,
                                        }}>
                                            {isDone ? <FiCheckCircle size={16} /> : stepNum}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '13px', fontWeight: 700, color: isActive ? 'var(--text-main)' : 'var(--text-muted)' }}>
                                                {s.label}
                                            </div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', opacity: 0.7 }}>
                                                {s.desc}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}

                            {/* Live preview card */}
                            {(newJob.title || newJob.type || newJob.location) && (
                                <div style={{
                                    marginTop: 'auto', padding: '16px',
                                    background: 'var(--bg-card)', borderRadius: '14px',
                                    border: '1px solid var(--border)',
                                }}>
                                    <p style={{ fontSize: '10px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                                        Preview
                                    </p>
                                    {newJob.title && <p style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-main)', margin: '0 0 6px' }}>{newJob.title}</p>}
                                    {newJob.type && <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', background: 'rgba(34,197,94,0.1)', color: 'var(--accent)', fontWeight: 700 }}>{newJob.type}</span>}
                                    {newJob.location && <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><FiMapPin size={10} /> {newJob.location}</p>}
                                    {newJob.stipend && <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><FiDollarSign size={10} /> {newJob.stipend}</p>}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── RIGHT PANEL: Form ── */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: isMobile ? '14px 16px' : '20px 28px',
                            borderBottom: '1px solid var(--border)',
                            flexShrink: 0,
                        }}>
                            <div>
                                {isMobile && (
                                    <p style={{ fontSize: '10px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 2px' }}>
                                        Step {step} of {TOTAL_STEPS}
                                    </p>
                                )}
                                <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: 900, color: 'var(--text-main)', margin: 0, letterSpacing: '-0.02em' }}>
                                    {STEPS[step - 1].label}
                                </h2>
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                                    {STEPS[step - 1].desc}
                                </p>
                            </div>
                            <motion.button
                                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                                onClick={onClose}
                                style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                                    color: 'var(--text-main)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <FiX size={18} />
                            </motion.button>
                        </div>

                        {/* Progress bar */}
                        <div style={{ display: 'flex', gap: '4px', padding: '0 28px', height: '3px', flexShrink: 0, background: 'var(--border)' }}>
                            <motion.div
                                animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                                transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                                style={{ height: '100%', background: 'var(--accent)', borderRadius: '2px' }}
                            />
                        </div>

                        {/* Scrollable form body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '20px 16px' : '28px 28px 16px' }}>
                            <form id="job-form" onSubmit={onSubmit}>
                                <AnimatePresence mode="wait">

                                    {/* STEP 1 — Basic Info */}
                                    {step === 1 && (
                                        <motion.div key="s1"
                                            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                                        >
                                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                                                <Field label="Job Title" required icon={<FiBriefcase />}>
                                                    <input
                                                        className="jpm-input"
                                                        value={newJob.title}
                                                        onChange={e => setNewJob({ ...newJob, title: e.target.value })}
                                                        placeholder="e.g. Software Engineer Intern"
                                                        style={inputStyle}
                                                        required
                                                    />
                                                </Field>
                                                <Field label="Location / Venue" required icon={<FiMapPin />}>
                                                    <input
                                                        className="jpm-input"
                                                        value={newJob.location}
                                                        onChange={e => setNewJob({ ...newJob, location: e.target.value })}
                                                        placeholder="e.g. Remote or New Delhi"
                                                        style={inputStyle}
                                                        required
                                                    />
                                                </Field>
                                            </div>

                                            <Field label="Job Type" required icon={<FiTag />} hint="Select the engagement type for this role.">
                                                <PillSelect
                                                    value={newJob.type}
                                                    onChange={v => setNewJob({ ...newJob, type: v })}
                                                    options={JOB_TYPES}
                                                />
                                            </Field>

                                            <Field label="Work Mode" icon={<FiMapPin />}>
                                                <PillSelect
                                                    value={newJob.workMode || 'Remote'}
                                                    onChange={v => setNewJob({ ...newJob, workMode: v })}
                                                    options={WORK_MODES}
                                                />
                                            </Field>
                                        </motion.div>
                                    )}

                                    {/* STEP 2 — Compensation & Details */}
                                    {step === 2 && (
                                        <motion.div key="s2"
                                            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                                        >
                                            {isDrive && (
                                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                                                    <Field label="Date & Time" required icon={<FiCalendar />}>
                                                        <input
                                                            className="jpm-input"
                                                            type="datetime-local"
                                                            value={newJob.dateTime || ''}
                                                            onChange={e => setNewJob({ ...newJob, dateTime: e.target.value })}
                                                            style={inputStyle}
                                                            required
                                                        />
                                                    </Field>
                                                    <Field label="Venue Details" icon={<FiMapPin />}>
                                                        <input
                                                            className="jpm-input"
                                                            placeholder="Building, floor, etc."
                                                            value={newJob.venue || ''}
                                                            onChange={e => setNewJob({ ...newJob, venue: e.target.value })}
                                                            style={inputStyle}
                                                        />
                                                    </Field>
                                                </div>
                                            )}
                                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                                                <Field label={isDrive ? 'Package / Stipend' : 'Stipend / Salary'} icon={<FiDollarSign />}>
                                                    <input
                                                        className="jpm-input"
                                                        placeholder="e.g. ₹10,000/month or LPA"
                                                        value={newJob.stipend || ''}
                                                        onChange={e => setNewJob({ ...newJob, stipend: e.target.value })}
                                                        style={inputStyle}
                                                    />
                                                </Field>
                                                <Field label="Duration" icon={<FiClock />}>
                                                    <input
                                                        className="jpm-input"
                                                        placeholder="e.g. 3 months or Full-time"
                                                        value={newJob.duration || ''}
                                                        onChange={e => setNewJob({ ...newJob, duration: e.target.value })}
                                                        style={inputStyle}
                                                    />
                                                </Field>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                                                <Field label="Category" icon={<FiTag />}>
                                                    <PillSelect
                                                        value={newJob.category || 'Engineering'}
                                                        onChange={v => setNewJob({ ...newJob, category: v })}
                                                        options={CATEGORIES}
                                                    />
                                                </Field>
                                                <Field label="Required Skills" icon={<FiCode />} hint="Separate with commas">
                                                    <input
                                                        className="jpm-input"
                                                        placeholder="e.g. React, Python, SQL"
                                                        value={newJob.skills || ''}
                                                        onChange={e => setNewJob({ ...newJob, skills: e.target.value })}
                                                        style={inputStyle}
                                                    />
                                                </Field>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* STEP 3 — Requirements */}
                                    {step === 3 && (
                                        <motion.div key="s3"
                                            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                                        >
                                            <Field label="Detailed Requirements" required icon={<FiUsers />} hint="Describe the role, responsibilities, eligibility, and process.">
                                                <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1.5px solid var(--border)' }}>
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={newJob.description}
                                                        onChange={val => setNewJob({ ...newJob, description: val })}
                                                        placeholder="Describe the role, responsibilities, and requirements..."
                                                        style={{ height: isMobile ? '200px' : '240px' }}
                                                        modules={{
                                                            toolbar: [
                                                                ['bold', 'italic', 'underline', 'strike'],
                                                                [{ list: 'ordered' }, { list: 'bullet' }],
                                                                ['link'], ['clean'],
                                                            ],
                                                        }}
                                                    />
                                                </div>
                                            </Field>
                                            <div style={{ height: '50px' }} />
                                            <div style={{
                                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                                padding: '14px 16px',
                                                background: 'rgba(234,179,8,0.06)',
                                                border: '1px solid rgba(234,179,8,0.2)',
                                                borderRadius: '12px',
                                            }}>
                                                <FiInfo size={14} color="#eab308" style={{ marginTop: '2px', flexShrink: 0 }} />
                                                <span style={{ fontSize: '12px', color: '#eab308', fontWeight: 600, lineHeight: 1.5 }}>
                                                    {isEditing
                                                        ? 'Changes are applied immediately (pending admin review).'
                                                        : 'Your job post will be reviewed by a SnapED admin before going live.'}
                                                </span>
                                            </div>
                                        </motion.div>
                                    )}

                                </AnimatePresence>
                            </form>
                        </div>

                        {/* ── Bottom Action Bar ── */}
                        <div style={{
                            display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'space-between',
                            padding: isMobile ? '14px 16px' : '16px 28px',
                            borderTop: '1px solid var(--border)',
                            background: 'var(--bg-main)',
                            flexShrink: 0,
                        }}>
                            <div>
                                {step > 1 && (
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                                        onClick={() => setStep(s => s - 1)}
                                        style={{
                                            height: '42px', padding: '0 18px', borderRadius: '11px',
                                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                                            color: 'var(--text-muted)', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            fontSize: '13px', fontWeight: 700,
                                        }}
                                    >
                                        <FiChevronLeft size={16} /> Back
                                    </motion.button>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                {/* Step dots */}
                                {!isMobile && (
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginRight: '8px' }}>
                                        {STEPS.map((_, idx) => (
                                            <div key={idx} style={{
                                                width: idx + 1 === step ? '20px' : '6px',
                                                height: '6px', borderRadius: '3px',
                                                background: idx + 1 === step ? 'var(--accent)' : idx + 1 < step ? 'var(--accent)' : 'var(--border)',
                                                transition: 'all 0.3s',
                                                opacity: idx + 1 < step ? 0.4 : 1,
                                            }} />
                                        ))}
                                    </div>
                                )}

                                {step < TOTAL_STEPS ? (
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                                        onClick={() => setStep(s => s + 1)}
                                        style={{
                                            height: '42px', padding: '0 22px', borderRadius: '11px',
                                            background: 'var(--accent)', border: 'none',
                                            color: '#fff', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            fontSize: '14px', fontWeight: 800,
                                            boxShadow: '0 4px 12px rgba(34,197,94,0.25)',
                                        }}
                                    >
                                        Continue <FiChevronRight size={16} />
                                    </motion.button>
                                ) : (
                                    <motion.button
                                        type="submit"
                                        form="job-form"
                                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                                        style={{
                                            height: '42px', padding: '0 24px', borderRadius: '11px',
                                            background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none',
                                            color: '#fff', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            fontSize: '14px', fontWeight: 800,
                                            boxShadow: '0 4px 16px rgba(34,197,94,0.3)',
                                        }}
                                    >
                                        <FiCheckCircle size={16} /> {isEditing ? 'Update Listing' : 'Post Opportunity'}
                                    </motion.button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default JobPostModal;
